/* eslint-disable max-len */
import { OverallResolverBase } from './OverallResolverBase';
import { ComponentFactory, ViewContainerRef } from '@angular/core';
import { ConfigDataService } from '../services/config-data.service';
import { GraphDataService } from '../services/graph-data-service.service';
import { ViewPeriodService } from '../services/view-period.service';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { GenericBarChartData } from '../objects/chart';
import { PopoverController } from '@ionic/angular';
import { DynamicGraphAdditionalInput } from '../objects/config';
import { PIE_CHART_COLOR_LIST } from '../configs/color';
import { GraphDependency } from '../enum/graph-dependency.enum';
import { getAdditionalInput } from '../helpers/DynamicGraphHelper';
import { assertNullUndefined } from '../helpers/util';
import { DynamicBarChartWrapperComponent } from '../shared-components/dynamic/dynamic-bar-chart-wrapper/dynamic-bar-chart-wrapper.component';
import { showGenericBarChartCustomTooltips } from '../helpers/barChartTooltips';

export class OverallAvgTimeSpentBarResolver extends OverallResolverBase {

    public get graphDependencyMap(): { building: GraphDependency[]; floor: GraphDependency[]; zone: GraphDependency[] } {
        return {
            building: [GraphDependency.ENTRANCE_EXIT],
            floor: [GraphDependency.ENTRANCE_EXIT_FLOOR],
            zone: [GraphDependency.ENTRANCE_EXIT_ZONE]
        };
    }

    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        _popoverController: PopoverController,
        _viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription,
    ) {
        // load data here
        const level = getAdditionalInput(additionalInput, 'level') as DynamicGraphAdditionalInput['level'] | undefined;
        const groupData = getAdditionalInput(additionalInput, 'groupData') as DynamicGraphAdditionalInput['groupData'] | undefined;
        assertNullUndefined(level);
        this.processDynamicDependency(configDataService, graphDataService, level);
        // await configDataService.loadAppConfig();

        type CurrentAvgTimeSpentData = { avgTimeSpent: number; diff: number; diffPercent: number };
        const getAvgTimeSpentData = (
            dataBuilding: { [buildingName: string]: CurrentAvgTimeSpentData },
            dataFloor: { [buildingName: string]: { [floorName: string]: CurrentAvgTimeSpentData } },
            dataZone: { [buildingName: string]: { [floorName: string]: { [zoneName: string]: CurrentAvgTimeSpentData } } },
        ) => this.getLeveledData<CurrentAvgTimeSpentData>(dataBuilding, dataFloor, dataZone, level);
        const sec2Min = (val: number | undefined | null) => val ? Math.round(val / 60) : val;
        const sec2MinFix = (val: number | undefined | null) => val ? (val / 60).toFixed(3) : val;
        const initialComparingNames = level === 'building' ? this.getComparingNames(graphDataService.baseGraphData.comparingBuildingState$.value) : (level === 'floor' ? this.getComparingNames(graphDataService.baseGraphData.comparingFloorState$.value) : this.getComparingNames(graphDataService.baseGraphData.comparingZoneState$.value));
        const initialData = getAvgTimeSpentData(graphDataService.currentBuildingAverageTimeSpentData$.value, graphDataService.currentAvgTimeSpentFloorData$.value, graphDataService.currentAvgTimeSpentZoneData$.value);
        let initialColor: string[] = [];
        let initialFilteredData = Object.entries(initialData).sort().filter(([name, _data], idx) => {
            if (initialComparingNames.includes(name)) {
                if (level === 'zone' && configDataService?.ZONE_LINE_CHART_COLOR_LIST) {
                    initialColor.push(configDataService.ZONE_LINE_CHART_COLOR_LIST[name]); 
                } else {
                    initialColor.push(PIE_CHART_COLOR_LIST[idx % PIE_CHART_COLOR_LIST.length]);
                }
                return true;
            }
            return false;
        });
        // let xAxisSuggestedTickMax: number = Math.max.apply(Math, initialFilteredData.map((o) => sec2Min(o[1].avgTimeSpent)));
        let xAxisSuggestedTickMax: number = Math.max.apply(Math, initialFilteredData.map((o) => sec2Min(o[1].avgTimeSpent)));
        if (this.configDataService.MAIN_BUILDING === `mbk` && level === 'floor' || 'zone') {
            initialFilteredData = initialFilteredData.sort().reverse();
            initialColor = initialColor.reverse();
        } else {
            initialFilteredData.sort();
        }        
        const chartData$ = new BehaviorSubject<GenericBarChartData[]>(initialFilteredData.map(([name, data], idx) => ({
            // data: initialFilteredData.map(([nameIt, _data]) => nameIt === name ? sec2Min(data.avgTimeSpent) : null),
            data: initialFilteredData.map(([nameIt, _data]) => nameIt === name ? sec2Min(data.avgTimeSpent) : null),
            color: initialColor[idx],
            // label: `${sec2MinFix(data)}`,
            label: `${sec2MinFix(data.avgTimeSpent)}`,
            calPercentFrommAllData: false,
        })));

        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicBarChartWrapperComponent;
        comInstance.chartLabel = initialFilteredData.map(([name, _data]) => this.getDisplayName(name));
        comInstance.data$ = chartData$;

        // eslint-disable-next-line max-len
        subscription.add(combineLatest([graphDataService.currentBuildingAverageTimeSpentData$, graphDataService.currentAvgTimeSpentFloorData$, graphDataService.currentAvgTimeSpentZoneData$, combineLatest([graphDataService.baseGraphData.comparingBuildingState$, graphDataService.baseGraphData.comparingFloorState$, graphDataService.baseGraphData.comparingZoneState$])]).subscribe(
            ([avgTimeSpentBuildingData, avgTimeSpentFloorData, avgTimeSpentZoneData, [comparingBuildingState, comparingFloorState, comparingZoneState]]) => {
                if ((!avgTimeSpentBuildingData && level === 'building') || (!avgTimeSpentFloorData && level === 'floor') || (!avgTimeSpentZoneData && level === 'zone')) { return; }
                const comparingNames = level === 'building' ? this.getComparingNames(comparingBuildingState) : (level === 'floor' ? this.getComparingNames(comparingFloorState) : this.getComparingNames(comparingZoneState));
                const fullData = getAvgTimeSpentData(avgTimeSpentBuildingData, avgTimeSpentFloorData, avgTimeSpentZoneData);
                if (groupData) {
                    Object.keys(groupData).forEach(k => {
                        const arrDataPoints: number[] = [];
                        groupData[k].args.forEach(arg => {
                            arrDataPoints.push(fullData[arg].avgTimeSpent);
                        });
                        fullData[k] = {avgTimeSpent: groupData[k].oper === 'AVG' ? arrDataPoints.length === 0 ? 0 : arrDataPoints.reduce(((a, b) => a + b), 0) / arrDataPoints.length : arrDataPoints.reduce(((a, b) => a + b), 0), diff: 0, diffPercent: 0};
                    });
                }
                let chartColors: string[] = [];
                let dataFiltered = Object.entries(fullData).sort().filter(([name, _data]) => {
                    if (comparingNames.includes(name)) {
                        if (level === 'zone' && configDataService?.ZONE_LINE_CHART_COLOR_LIST) {
                            chartColors.push(configDataService.ZONE_LINE_CHART_COLOR_LIST[name]); 
                        } else {
                            chartColors.push('#6593F5');
                        }
                        return true;
                    }
                    return false;
                });
            xAxisSuggestedTickMax = Math.max.apply(Math, dataFiltered.map((o) => sec2Min(o[1].avgTimeSpent)));
                if (configDataService.MAIN_BUILDING === `mbk` && level === 'floor' || 'zone') {
                    dataFiltered = dataFiltered.sort().reverse();
                    chartColors = chartColors.reverse();
                } else {
                    dataFiltered.sort();
                }
                const barChartData: GenericBarChartData[] = dataFiltered.map(([name, data], idx) => ({
                    data: dataFiltered.map(([nameIt, _data]) => nameIt === name ? sec2Min(data.avgTimeSpent) : null),
                    color: chartColors[idx],
                    label: `${sec2MinFix(data.avgTimeSpent)}`,
                    calPercentFrommAllData: false,
                }));
                chartData$.next(barChartData);
                comInstance.chartLabel = dataFiltered.map(([name, _data]) => this.getDisplayName(name));
            }));
        comInstance.isLock = this.isLock;
        comInstance.title = configDataService.DISPLAY_LANGUAGE.DURATION_VISIT_NUMBER_CARD;
        comInstance.isShowLegend = false;
        comInstance.isHorizontal = true;
        comInstance.valueTextAlign = 'left';
        comInstance.valueTextOffsetX = 3;
        comInstance.valueTextOffsetY = 5;
        comInstance.valueTextProcess = 'toLocaleString';
        comInstance.displayAxis = 'Y';
        comInstance.isStacked = 'Y';
        comInstance.displayGrid = false;
        comInstance.suggestedTickMax_X = xAxisSuggestedTickMax + ( xAxisSuggestedTickMax / 10);
        comInstance.customToolTipFuction = function(tooltipModel) {
            showGenericBarChartCustomTooltips(tooltipModel, this);
        };
        return comInstance;
    }

}
